import { Box, Typography } from '@mui/material'
import { SearchConditionAndColumnState } from '../../../../services/model/searchConditionAndColumnState'
import { TaskActualWorkProxyRegistrationCondition } from '../hooks/useSearchCondition'
import { colorPalette } from '../../../style/colorPalette'
import { FontSize, FontWeight } from '../../../../styles/commonStyles'
import { intl } from '../../../../i18n'
import { Button } from '../../../components/buttons'
import SaveSearchConditionListDialog from '../../WbsItemSearch/components/Header/SaveSearchConditionListDialog'
import { useState, useCallback } from 'react'
import { WBS_ITEM_SEARCH_COUNT_LIMIT } from '../../WbsItemSearch/wbsItemSearch'
import { PermanentUiState } from '../../../../services/model/uiState'
import { TaskActualWorkProxyRegistrationDialog } from './TaskActualWorkProxyRegistrationDialog'
import { WbsItemSearchItemLabels } from './WbsItemSearchSearchHeader/WbsItemSearchItemLabels'
import styled from 'styled-components'

type Props = {
  hit: number
  count: number
  total: number
  searchCondition: TaskActualWorkProxyRegistrationCondition
  onChange: (patch: Partial<TaskActualWorkProxyRegistrationCondition>) => void
  search: () => void
  resetCondition: () => void
  functionUuid: string
  applyStoredState: (storedState: SearchConditionAndColumnState) => void
  onCloseSearchItemLabel: () => void
  isLoading: boolean
  isMemberSelected: boolean
}

export const TaskActualWorkProxyRegistrationConditionHeader = ({
  hit,
  count,
  total,
  isLoading,
  searchCondition,
  onChange,
  search,
  resetCondition,
  functionUuid,
  applyStoredState,
  onCloseSearchItemLabel,
  isMemberSelected,
}: Props) => {
  const [openSearchConditionPanel, setOpenSearchConditionPanel] =
    useState<boolean>(false)
  const [openSavedSearchConditionPanel, setOpenSavedSearchConditionPanel] =
    useState<boolean>(false)
  const [filteringInput, setFilteringInput] = useState<string>('')
  const handleSearchConditionPanelOpen = () => {
    setOpenSearchConditionPanel(true)
  }
  const handleSavedSearchConditionPanelOpen = () => {
    setOpenSavedSearchConditionPanel(true)
  }
  const handleSavedSearchConditionPanelClose = () => {
    setOpenSavedSearchConditionPanel(false)
  }

  const onSearch = useCallback(() => {
    search()
    setOpenSearchConditionPanel(false)
  }, [search])
  const onSelectState = useCallback(
    (selected: PermanentUiState<SearchConditionAndColumnState>) => {
      applyStoredState(selected.value)
    },
    [applyStoredState]
  )

  const handleSearchConditionPanelClose = () => {
    setOpenSearchConditionPanel(false)
    // 一旦1万件以上の場合はダイアログを閉じても検索は行わない
    if (count <= WBS_ITEM_SEARCH_COUNT_LIMIT) {
      search()
    }
  }

  return (
    <>
      <SearchConditionHeaderDiv>
        <WbsItemSearchBox>
          <FilteringItemsLabelTypography>
            {intl.formatMessage({
              id: 'wbsItemSearchFilterPanel.filteringItems.label',
            })}
          </FilteringItemsLabelTypography>
          <SearchResultBox>
            <SearchResultTypography>
              {intl.formatMessage({
                id: 'wbs.search.header.search.panel.result',
              })}
            </SearchResultTypography>
            <SearchHitsTypography>
              {intl.formatMessage(
                { id: 'wbs.search.header.search.hits' },
                { hit }
              )}
            </SearchHitsTypography>
            <SlashTypography>/</SlashTypography>
            <SearchTotalTypography>
              {intl.formatMessage(
                { id: 'wbs.search.header.search.total' },
                { total }
              )}
            </SearchTotalTypography>
          </SearchResultBox>
          <AddConditionButton
            onClick={handleSearchConditionPanelOpen}
            colorPattern="skyBlue"
            disabled={isLoading || !isMemberSelected}
          >
            <AddConditionButtonTypography>
              {intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.add.condition',
              })}
            </AddConditionButtonTypography>
          </AddConditionButton>
          <SavedSearchConditionButton
            onClick={handleSavedSearchConditionPanelOpen}
            colorPattern="monotone"
            variant="outlined"
            disabled={!isMemberSelected}
          >
            <SavedSearchConditionButtonTypography>
              {intl.formatMessage({
                id: 'wbsItemSearchFilterPanel.saved.condition',
              })}
            </SavedSearchConditionButtonTypography>
          </SavedSearchConditionButton>
        </WbsItemSearchBox>
        <WbsItemSearchItemLabels
          searchCondition={searchCondition}
          onChange={onChange}
          search={search}
          onClose={onCloseSearchItemLabel}
          isLoading={isLoading}
        />
      </SearchConditionHeaderDiv>
      <TaskActualWorkProxyRegistrationDialog
        count={count}
        open={openSearchConditionPanel}
        onClose={handleSearchConditionPanelClose}
        searchCondition={searchCondition}
        onEnter={search}
        onChangeCondition={onChange}
        resetCondition={resetCondition}
        search={onSearch}
        filteringInput={filteringInput}
        setFilteringInput={setFilteringInput}
        onSelectState={onSelectState}
        functionUuid={functionUuid}
      />
      <SaveSearchConditionListDialog
        open={openSavedSearchConditionPanel}
        handleSavedSearchConditionPanelClose={
          handleSavedSearchConditionPanelClose
        }
        filteringInput={filteringInput}
        setFilteringInput={setFilteringInput}
        onSelectState={onSelectState}
        functionUuid={functionUuid}
        searchCondition={searchCondition}
      />
    </>
  )
}

const SearchConditionHeaderDiv = styled('div')({
  display: 'flex',
  borderRadius: '4px',
  padding: '10px',
  backgroundColor: 'rgba(216, 221, 229, 0.3)',
  margin: '10px 20px 10px 20px',
  flexDirection: 'column',
  height: 'fit-content',
})

const WbsItemSearchBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const SearchResultBox = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  marginRight: '20px',
  marginLeft: '10px',
})

const SearchTotalTypography = styled(Typography)({
  color: colorPalette.monotone[4],
  fontSize: FontSize.MEDIUM,
  fontWeight: 500,
  margin: '5px 0 0 0',
})

const SlashTypography = styled(Typography)({
  color: colorPalette.monotone[4],
  fontSize: FontSize.MEDIUM,
  fontWeight: 500,
  margin: '5px 5px 0 5px',
})

const SearchHitsTypography = styled(Typography)({
  color: colorPalette.monotone[10],
  fontWeight: 700,
  fontSize: FontSize.LARGE,
})

const SearchResultTypography = styled(Typography)({
  color: colorPalette.monotone[4],
  fontSize: FontSize.MEDIUM,
  fontWeight: FontWeight.NORMAL,
  marginTop: '1px',
  marginRight: '10px',
})

const FilteringItemsLabelTypography = styled(Typography)({
  fontSize: FontSize.LARGE,
  fontWeight: 700,
  color: colorPalette.monotone[10],
  marginRight: '20px',
  marginLeft: '10px',
})

const AddConditionButton = styled(Button)({
  border: 'none',
  margin: '0 5px',
  cursor: 'pointer',
})

const AddConditionButtonTypography = styled(Typography)({
  fontSize: '12px',
})

const SavedSearchConditionButton = styled(Button)({
  margin: '0 5px',
  cursor: 'pointer',
})

const SavedSearchConditionButtonTypography = styled(Typography)({
  fontSize: '12px',
})
